import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Card,
  Typography,
  Box,
  Chip,
  SxProps,
  IconButton,
  Tooltip
} from "@mui/material";
import { ErrorPanel, formatDateTime, useToast } from "@qubit/autoparts";
import { useEffect } from "react";

import { useAppSelector } from "~/app/store";
import { selectFetchStatusEndpointsInterval } from "~/features/andon/andon.slice";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { useGetDevicesHealthQuery } from "~/redux/public/deviceHealth";

const PTLCardSx: SxProps = {
  p: 2,
  mb: 2,
  minWidth: 300,
  maxWidth: 400,
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  boxShadow: 1
};

const copyToClipboard = (text: string) => {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).catch(() => {
      // do nothing
    });
  }
};

const ShortenedIdDisplay = ({ id, label }: { id: string; label: string }) => {
  const shortenedId =
    id.length > 12 ? `${id.slice(0, 8)}...${id.slice(-4)}` : id;

  return (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Typography variant="body2" color="text.secondary">
        {label}:
      </Typography>
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography variant="body2">{shortenedId}</Typography>
        <Tooltip title={id}>
          <IconButton
            aria-label={`Copy ${label}`}
            size="small"
            onClick={() => copyToClipboard(id)}
            sx={{ p: 0.25 }}
          >
            <ContentCopyIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export const PTLHealthCard = () => {
  const fetchStatusEndpointsInterval = useAppSelector(
    selectFetchStatusEndpointsInterval
  );

  const {
    data: devicesHealth,
    refetch: refetchDevicesHealth,
    error: devicesHealthError,
    isLoading
  } = useGetDevicesHealthQuery(undefined, {
    pollingInterval:
      fetchStatusEndpointsInterval > 0 ? fetchStatusEndpointsInterval : 5000
  });

  const { errorToast } = useToast();

  useEffect(() => {
    if (devicesHealthError) {
      errorToast(getMessageFromRtkError(devicesHealthError));
    }
  }, [devicesHealthError, errorToast]);

  // Group devices by type
  const allDevices = devicesHealth ?? [];
  const devicesByType = allDevices.reduce<Record<string, typeof allDevices>>(
    (acc, device) => {
      const type = device.deviceAdapterType;
      const currentTypeDevices = acc[type] || [];
      return {
        ...acc,
        [type]: [...currentTypeDevices, device]
      };
    },
    {}
  );

  // Calculate overall statistics
  const totalDevices = allDevices.length;
  const connectedDevices = allDevices.filter(
    (device) => device.connected
  ).length;
  const allConnected = totalDevices > 0 && connectedDevices === totalDevices;
  const hasDisconnected = connectedDevices < totalDevices;

  return (
    <Card sx={PTLCardSx}>
      {/* Header with Device Status */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6" component="h1">
          Device Health
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: allConnected
                ? "success.main"
                : hasDisconnected
                  ? "error.main"
                  : "warning.main"
            }}
          />
          <Typography variant="body2" color="text.secondary">
            {totalDevices === 0
              ? "No Devices"
              : allConnected
                ? "All Connected"
                : "Issues Detected"}
          </Typography>
        </Box>
      </Box>

      {/* Device Details */}
      <Box display="flex" flexDirection="column" gap={1}>
        {devicesHealthError && (
          <ErrorPanel
            message={getMessageFromRtkError(devicesHealthError)}
            tryAgain={{
              onTryAgain: async () => {
                await refetchDevicesHealth();
              }
            }}
          />
        )}

        <Box display="flex" justifyContent="space-between">
          <Typography variant="body2" color="text.secondary">
            Total Devices:
          </Typography>
          <Typography variant="body2">
            {connectedDevices} / {totalDevices} Connected
          </Typography>
        </Box>

        {/* Device List by Type */}
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={2}>
            <Typography variant="body2" color="text.secondary">
              Loading device status...
            </Typography>
          </Box>
        ) : (
          Object.entries(devicesByType).map(([, devices]) =>
            devices.map((device) => {
              const isConnected = device.connected;
              return (
                <Box
                  key={device.deviceId}
                  display="flex"
                  flexDirection="column"
                  gap={0.5}
                  py={1}
                  pl={2}
                  sx={{
                    borderLeft: "3px solid",
                    borderLeftColor: "divider",
                    ml: 1,
                    mb: 1
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    color="text.primary"
                  >
                    {device.deviceName}
                  </Typography>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Type:
                    </Typography>
                    <Typography variant="body2">
                      {device.deviceAdapterType}
                    </Typography>
                  </Box>
                  <ShortenedIdDisplay id={device.deviceId} label="ID" />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Status:
                    </Typography>
                    <Chip
                      label={isConnected ? "Running" : "Stopped"}
                      size="small"
                    />
                  </Box>
                  {!isConnected && device.connectionLostTime && (
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Connection Lost:
                      </Typography>
                      <Typography variant="body2">
                        {formatDateTime(new Date(device.connectionLostTime))}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })
          )
        )}

        {totalDevices === 0 && !isLoading && (
          <Box display="flex" justifyContent="center" py={2}>
            <Typography variant="body2" color="text.secondary">
              No devices configured
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
};
